import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, Loader2, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, STORE_ADDRESS, STORE_CITY, waLink } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", phone: "", interest: "Rações", message: "" };
const INTERESTS = ["Rações", "Petshop", "Campo & Pecuária", "Jardim & Agro", "Banho & Tosa"];

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada!", {
        description: "A equipe Agro Carmo retorna em breve. Obrigado!",
      });
      setForm(INITIAL);
    } catch {
      toast.error("Não foi possível enviar", {
        description: "Tente novamente ou chame a gente no WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="bg-white/50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-clay">
            Fale com a gente
          </p>
          <h2 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Pedido, dúvida ou só um prosa?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <motion.form
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="rounded-[2rem] border border-linen bg-white p-8 shadow-sm lg:col-span-3 sm:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">Nome completo</span>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Seu nome"
                  data-testid="contact-name-input"
                  className="w-full rounded-xl border border-linen bg-parchment px-4 py-3 text-sm outline-none transition-colors placeholder:text-moss/60 focus:border-clay"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">Telefone / WhatsApp</span>
                <input
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(00) 90000-0000"
                  data-testid="contact-phone-input"
                  className="w-full rounded-xl border border-linen bg-parchment px-4 py-3 text-sm outline-none transition-colors placeholder:text-moss/60 focus:border-clay"
                />
              </label>
            </div>
            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-medium text-ink">Tipo de interesse</span>
              <select
                value={form.interest}
                onChange={update("interest")}
                data-testid="contact-interest-select"
                className="w-full appearance-none rounded-xl border border-linen bg-parchment px-4 py-3 text-sm outline-none transition-colors focus:border-clay"
              >
                {INTERESTS.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </label>
            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-medium text-ink">Mensagem / Pedido</span>
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Ex.: 2 sacos de ração premium 15kg para entrega no sítio..."
                data-testid="contact-message-input"
                className="w-full resize-none rounded-xl border border-linen bg-parchment px-4 py-3 text-sm outline-none transition-colors placeholder:text-moss/60 focus:border-clay"
              />
            </label>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-olive px-6 py-4 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-forest disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Enviando..." : "Enviar mensagem"}
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex flex-col gap-8 rounded-[2rem] bg-forest p-8 text-parchment sm:p-10 lg:col-span-2"
          >
            <div>
              <h3 className="font-display text-2xl font-medium">Visite a loja</h3>
              <p className="mt-2 text-sm text-parchment/65">
                Estoque cheio, gente boa e cheiro de ração nova. Do jeito que loja de campo tem que ser.
              </p>
            </div>
            <ul className="space-y-5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ochre" />
                <span data-testid="contact-address">
                  {STORE_ADDRESS}
                  <br />
                  {STORE_CITY}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-ochre" />
                <span data-testid="contact-hours">Seg a Sáb — 9h às 20h</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-ochre" />
                <span data-testid="contact-phone">{PHONE_DISPLAY}</span>
              </li>
            </ul>
            <a
              href={waLink("Olá, Agro Carmo! Vim pelo site e quero fazer um pedido.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
            >
              <MessageCircle className="h-5 w-5" />
              Chamar no WhatsApp
            </a>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
